<script setup lang="ts">
defineOptions({ inheritAttrs: false });

import { type Ref, computed, ref, onMounted } from "vue";
import { useHandlePost } from "@/hooks";
import { debounce } from "@/utils";

interface FilterRow {
  FieldKey: string;
  Operator: string;
  Value: any;
}

const props = defineProps<{
  pageSize: number;
  columnNum: number;
  gutter: number;
  autoLoad: boolean;
  infiniteThreshold: number;
  infiniteUseCapture: boolean;
  infiniteLoadMoreTxt: string;
  infiniteLoadTxt: string;
}>();

const emit = defineEmits(["itemClick"]);
const { caller } = useHandlePost();

const refList = ref();
const list: Ref<Record<string, any>[]> = ref([]);
const pageNo = ref(1);
const total = ref(0);
const hasMore = computed(() => total.value > list.value.length);
const filterRows: Ref<FilterRow[] | undefined> = ref();
const infinityValue = ref(false);
const refreshValue = ref(false);

function setTotal(val: number) {
  total.value = val;
}

function setFilterRows(filter?: FilterRow[]) {
  filterRows.value = filter;
}

function addRows(rows: Record<string, any>[]) {
  list.value = list.value.concat(rows);
}

async function loadMore() {
  pageNo.value++;
  await loadData();
  infinityValue.value = false;
}
async function refresh() {
  await reload();
  refreshValue.value = false;
}

async function reload() {
  pageNo.value = 1;
  total.value = 0;
  list.value = [];
  await loadData();
}

const loadData = debounce(async () => {
  await caller("LoadListData", {
    pageNo: pageNo.value,
    pageSize: props.pageSize,
    filterRows: filterRows.value,
  });
}, 500);

function setChildrenStyle(rowIndex: number, key: string, style: any) {
  if (!refList.value) return;
  refList.value.setChildrenStyle(rowIndex, key, style);
}

function setChildrenProps(rowIndex: number, key: string, props: any) {
  if (!refList.value) return;
  refList.value.setChildrenProps(rowIndex, key, props);
}

function clearChildrenStyles() {
  if (!refList.value) return;
  refList.value.clearChildrenStyles();
}

function clearChildrenProps() {
  if (!refList.value) return;
  refList.value.clearChildrenProps();
}

onMounted(() => {
  if (props.autoLoad) {
    loadData();
  }
});

defineExpose({
  reload,
  setTotal,
  setFilterRows,
  addRows,
  setChildrenStyle,
  setChildrenProps,
  clearChildrenStyles,
  clearChildrenProps,
});
</script>
<template>
  <nut-infinite-loading
    class="mp-infinite-loading-list"
    v-model="infinityValue"
    :has-more="hasMore"
    :threshold="infiniteThreshold"
    :use-capture="infiniteUseCapture"
    :load-more-txt="infiniteLoadMoreTxt"
    :load-txt="infiniteLoadTxt"
    @load-more="loadMore"
  >
    <nut-pull-refresh v-bind="$attrs" v-model="refreshValue" @refresh="refresh">
      <MpList
        ref="refList"
        class="mp-infinite-loading-list__content"
        :data="list"
        :column-num="columnNum"
        :gutter="gutter"
        direction="row"
        padding="0"
        @itemClick="emit('itemClick', $event)"
      >
        <slot />
      </MpList>
    </nut-pull-refresh>
  </nut-infinite-loading>
</template>
<style lang="scss">
.mp-infinite-loading-list {
  &__content {
    height: auto;
  }
}
</style>
